import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { CheckCircle, Star, Code } from 'lucide-react';
import { Button } from '../ui/button';

const AuthoritySection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const handleCTAClick = () => {
    const phone = '5521976614158';
    const message = 'Olá Hugo! Vi sua experiência impressionante e quero conversar sobre meu projeto.';
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const experiences = [
    'Mais de 10 anos criando soluções digitais',
    'Especialista em conversão e performance',
    'Experiência em e-commerce e landing pages',
    'Foco total em resultados mensuráveis',
  ];

  const companies = [
    'americanas s.a.',
    'Grupo Boticário',
    'Calvin Klein',
    'Taco',
  ];

  return (
    <section ref={ref} className="py-24 bg-white">
      <div className="container mx-auto px-6 lg:px-20">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left - Image */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="https://customer-assets.emergentagent.com/job_hugo-sites-pro/artifacts/pb8j3pcl_Foto%20profissional%20AI%20Cropped.png"
                alt="Hugo Renzi - Especialista em Presença Digital e Desenvolvimento Web"
                className="w-full h-auto object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
            </div>

            {/* Floating Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="absolute -bottom-6 -right-6 bg-white p-6 rounded-2xl shadow-xl border-2 border-blue-100"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                  <Star className="text-white" fill="white" size={24} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">10+</p>
                  <p className="text-sm text-gray-600">Anos de experiência</p>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right - Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="space-y-6"
          >
            <div>
              <span className="bg-blue-100 text-blue-600 px-4 py-2 rounded-full text-sm font-semibold">
                QUEM SOU EU
              </span>
            </div>

            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">
              Hugo Renzi
            </h2>

            <p className="text-xl text-gray-600 leading-relaxed">
              Especialista em presença digital e desenvolvimento web
            </p>

            <p className="text-lg text-gray-600 leading-relaxed">
              Com mais de uma década de experiência, transformo negócios através de landing pages estratégicas e focadas em conversão. Minha missão é fazer seu site trabalhar para você 24/7.
            </p>

            {/* Experience List */}
            <div className="space-y-4 pt-4">
              {experiences.map((exp, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  className="flex items-start gap-3"
                >
                  <CheckCircle className="text-blue-600 flex-shrink-0 mt-1" size={20} />
                  <p className="text-gray-700">{exp}</p>
                </motion.div>
              ))}
            </div>

            {/* Companies Experience */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.7 }}
              className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-2xl border border-blue-100"
            >
              <div className="flex items-center gap-3 mb-4">
                <Code className="text-blue-600" size={24} />
                <p className="font-semibold text-gray-900">Experiência em grandes projetos</p>
              </div>
              <p className="text-gray-600 text-sm mb-3">
                Participei de projetos de grandes empresas:
              </p>
              <div className="flex flex-wrap gap-2">
                {companies.map((company, index) => (
                  <span
                    key={index}
                    className="bg-white px-3 py-1.5 rounded-lg text-sm font-medium text-gray-700 shadow-sm"
                  >
                    {company}
                  </span>
                ))}
              </div>
            </motion.div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.9 }}
            >
              <Button
                onClick={handleCTAClick}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 rounded-full text-lg font-semibold transition-all duration-300 hover:shadow-xl hover:scale-105"
              >
                Falar com Hugo Renzi
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AuthoritySection;
